import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { buildGoogleCalendarUrl, type CalendarEventData } from '@/lib/calendarUtils';
import { sanitizeFilename, safeContentType } from '@/lib/sanitizeFilename';
import { DELIVERABLE_FILE_ACCEPT, validateDeliverableUpload } from '@/lib/deliverableUpload';

import { PendingItemsWidget } from '@/components/PendingItemsWidget';
import { useWeeklyProgress } from '@/hooks/useWeeklyProgress';

import { format, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { CalendarEvent, CalendarEventType, Expert, SOLUTION_TYPES } from '@/data/constants';
import { useCohortSchedule, useWorkshopTools } from '@/hooks/useCalendar';
import { useSelectedCohort } from '@/hooks/useSelectedCohort';
import { useDeliverables, useCreateDeliverable, DeliverableRow } from '@/hooks/useDeliverables';
import { useAIToolsCatalog } from '@/hooks/useAIToolsCatalog';
import { useChaptersWithContent, useAllContentProgress, useUpdateContentProgress } from '@/hooks/useContent';
import { ContentPlayerModal, type ContentType as PlayerContentType } from '@/components/ContentPlayerModal';
import { getCurrentWeek } from '@/hooks/useWeeklyProgress';
import { CertificationHeader } from '@/components/CertificationHeader';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Video, Play, Upload, Calendar as CalendarIcon, Users, User, ExternalLink, CheckCircle2, HelpCircle, BookOpen, FileText, Filter, Link2, Linkedin, X, Award, ChevronDown, Clock, PlayCircle, Wrench, AlertCircle, ClipboardList, RefreshCw, Loader2, Beaker, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ModuleOnboarding from '@/components/ModuleOnboarding';
import { calendarOnboardingSteps, calendarOnboardingBenefits } from '@/data/onboardingConfigs';

type EventTypeFilter = 'all' | CalendarEventType;
type WeekFilter = 'all' | number;

function DeliverableCard({ d }: { d: DeliverableRow }) {
  const [expanded, setExpanded] = useState(false);
  const commentText = d.description || d.comment;
  return (
    <div className={cn(
      "p-3 rounded-lg border",
      d.status === 'needs_revision' ? "bg-warning/10 border-warning/20" : "bg-success/10 border-success/20"
    )}>
      <div className="flex items-center gap-2 text-sm font-medium">
        {d.status === 'needs_revision' ? (
          <><AlertCircle className="w-4 h-4 text-warning" /><span className="text-warning">Requiere ajustes</span></>
        ) : (
          <><CheckCircle2 className="w-4 h-4 text-success" /><span className="text-success">Entregado</span></>
        )}
      </div>
      {d.name && <p className="text-xs font-medium text-foreground mt-1 truncate">{d.name}</p>}
      {d.submitted_at && (
        <p className="text-xs text-muted-foreground mt-0.5">
          {format(new Date(d.submitted_at), "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es })}
        </p>
      )}
      {commentText && (
        <div className="mt-1.5">
          <p className="text-xs font-medium text-foreground">Comentario</p>
          <p className={cn("text-xs text-muted-foreground italic", !expanded && "line-clamp-3")}>
            "{commentText}"
          </p>
          {commentText.length > 120 && (
            <button onClick={() => setExpanded(e => !e)}
              className="text-xs text-primary mt-0.5 hover:underline">
              {expanded ? 'Ver menos' : 'Ver más'}
            </button>
          )}
        </div>
      )}
      {(d.file_urls && d.file_urls.length > 0 ? d.file_urls : d.file_url ? [d.file_url] : []).map((url, i) => {
        const raw = url.split('/').pop()?.split('?')[0] ?? '';
        const label = decodeURIComponent(raw).replace(/_20/g, ' ') || `Archivo ${i + 1}`;
        return (
          <Button key={i} variant="link" size="sm" className="p-0 h-auto mt-1.5 text-primary flex items-center gap-1 text-left"
            onClick={() => window.open(url, '_blank')}>
            <ExternalLink className="w-3 h-3 shrink-0" />
            <span className="truncate max-w-[220px]">{label}</span>
          </Button>
        );
      })}
      {d.status === 'needs_revision' && d.feedback && (
        <div className="mt-2 p-2 rounded bg-muted/50">
          <p className="text-xs text-muted-foreground italic">"{d.feedback}"</p>
        </div>
      )}
    </div>
  );
}

const CalendarPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Shared cohort selection
  const { enrollments, selectedCohortId, setSelectedCohortId, activeEnrollment, selectedProgramId } = useSelectedCohort();
  
  // Fetch real schedule data
  const { data: scheduleItems } = useCohortSchedule(selectedCohortId);
  
  // Fetch real chapters + content for the program
  const { data: chapters = [] } = useChaptersWithContent(selectedProgramId);
  const { data: contentProgress = [] } = useAllContentProgress();
  const updateContentProgress = useUpdateContentProgress();

  // Optimistic overrides for progress updated from the in-calendar player
  const [progressOverrides, setProgressOverrides] = useState<Record<string, { completed: boolean; progress_percent: number }>>({});

  // In-calendar content player state
  const [playerLesson, setPlayerLesson] = useState<any | null>(null);
  const [playerLessonIndex, setPlayerLessonIndex] = useState(0);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  // Build a set of completed content IDs
  const completedContentIds = useMemo(() => {
    const set = new Set(contentProgress.filter(p => p.completed).map(p => p.content_id));
    Object.entries(progressOverrides).forEach(([id, o]) => {
      if (o.completed) set.add(id); else set.delete(id);
    });
    return set;
  }, [contentProgress, progressOverrides]);
  
  // Build progress map (content_id -> progress_percent)
  const progressMap = useMemo(() => {
    const map = new Map<string, number>();
    contentProgress.forEach(p => map.set(p.content_id, p.progress_percent ?? 0));
    Object.entries(progressOverrides).forEach(([id, o]) => map.set(id, o.progress_percent));
    return map;
  }, [contentProgress, progressOverrides]);

  const handleOpenLessonInCalendar = (lesson: any, index: number) => {
    setPlayerLesson(lesson);
    setPlayerLessonIndex(index);
    setIsPlayerOpen(true);
  };

  const handleClosePlayer = () => {
    setIsPlayerOpen(false);
    setPlayerLesson(null);
    queryClient.invalidateQueries({ queryKey: ['all-content-progress'] });
    queryClient.invalidateQueries({ queryKey: ['content-progress'] });
    queryClient.invalidateQueries({ queryKey: ['weekly-progress'] });
  };

  const handleLessonComplete = (lessonId: string) => {
    setProgressOverrides(prev => ({ ...prev, [lessonId]: { completed: true, progress_percent: 100 } }));
    updateContentProgress.mutate({ content_id: lessonId, completed: true, progress_percent: 100 });
  };

  const handleProgressSaved = (p: { contentId: string; completed: boolean; progressPercent: number }) => {
    setProgressOverrides(prev => ({
      ...prev,
      [p.contentId]: { completed: p.completed, progress_percent: p.progressPercent },
    }));
    queryClient.invalidateQueries({ queryKey: ['all-content-progress'] });
    queryClient.invalidateQueries({ queryKey: ['content-progress'] });
    queryClient.invalidateQueries({ queryKey: ['weekly-progress'] });
  };
  
  // Fetch existing deliverables from DB
  const { data: existingDeliverables = [] } = useDeliverables();
  const createDeliverable = useCreateDeliverable();
  
  // Map of workshop_ids to deliverable info for participant view
  const submittedDeliverablesMap = useMemo(() => {
    const map = new Map<string, { status: string; feedback: string | null; file_url: string | null; submitted_at: string | null }>();
    for (const d of existingDeliverables) {
      if (d.workshop_id && (d.status === 'submitted' || d.status === 'validated' || d.status === 'needs_revision')) {
        map.set(d.workshop_id, { status: d.status, feedback: d.feedback, file_url: d.file_url, submitted_at: d.submitted_at });
      }
    }
    return map;
  }, [existingDeliverables]);

  // Keep backward-compat set for deliverableUploaded boolean
  const submittedWorkshopIds = useMemo(() => {
    const set = new Set<string>();
    for (const [workshopId] of submittedDeliverablesMap) {
      set.add(workshopId);
    }
    return set;
  }, [submittedDeliverablesMap]);

  // Calculate current week from cohort start date (or plan start for self-paced routes)
  const isSelfPacedCohort = !!activeEnrollment?.is_perpetual;
  const currentProgramWeek = useMemo(() => {
    if (isSelfPacedCohort) {
      return getCurrentWeek(activeEnrollment?.plan_start_at ?? undefined);
    }
    return getCurrentWeek(activeEnrollment?.week_start_date ?? undefined);
  }, [isSelfPacedCohort, activeEnrollment?.plan_start_at, activeEnrollment?.week_start_date]);

  // Derive available weeks from chapters
  const availableWeeks = useMemo(() => {
    const weekSet = new Set<number>();
    chapters.forEach(ch => { if (ch.week_number) weekSet.add(ch.week_number); });
    (scheduleItems ?? []).forEach(item => { if (item.week_number) weekSet.add(item.week_number); });
    return Array.from(weekSet).sort((a, b) => a - b);
  }, [chapters, scheduleItems]);

  // Challenge Lab pending status — completed if at least 1 challenge AND 1 solution
  const { data: calChallengeLabData } = useQuery({
    queryKey: ['calendar-challenge-lab-status', user?.id],
    queryFn: async () => {
      const { data: challenges } = await supabase.from('challenges').select('id').eq('user_id', user!.id);
      const count = challenges?.length || 0;
      let solutions = 0;
      if (count > 0) {
        const { count: solCount } = await supabase.from('challenge_solutions').select('id', { count: 'exact', head: true }).in('challenge_id', challenges!.map(c => c.id));
        solutions = solCount || 0;
      }
      return { challenges: count, solutions, completed: count > 0 && solutions > 0 };
    },
    enabled: !!user?.id,
  });

  // Project status — done if any project in this cohort is submitted or completed
  const { data: calProjectData } = useQuery({
    queryKey: ['calendar-project-status', user?.id, selectedCohortId],
    queryFn: async () => {
      let q = supabase.from('participant_projects').select('id, submitted, status').eq('user_id', user!.id);
      if (selectedCohortId) q = q.eq('cohort_id', selectedCohortId);
      const { data } = await q;
      const completed = (data ?? []).some(p => p.submitted === true || p.status === 'completed');
      return { completed };
    },
    enabled: !!user?.id,
  });

  // Weekly progress for pending content/deliverables (shared with /dashboard)
  const { data: weeklyData = [] } = useWeeklyProgress(activeEnrollment?.program_id, activeEnrollment?.week_start_date ?? undefined, activeEnrollment?.cohort_id, {
    project_deadline: activeEnrollment?.project_deadline,
    project_deadline_week: activeEnrollment?.project_deadline_week,
    challenge_lab_deadline: activeEnrollment?.challenge_lab_deadline,
    challenge_lab_deadline_week: activeEnrollment?.challenge_lab_deadline_week,
  });
  const { pendingContent, pendingDeliverables, pendingDeliverableList } = useMemo(() => {
    let pendCont = 0;
    let pendDel = 0;
    const deliverableList: { weekTitle: string; title: string; refId?: string }[] = [];
    for (const week of weeklyData) {
      for (const task of week.tasks) {
        if (!task.isActive) continue;
        if (task.type === 'content') pendCont += task.total - task.completed;
        // No contar como pendiente el entregable cuya sesión en vivo aún no se dictó.
        if (task.type === 'deliverable' && !task.isComplete && !task.availableAfterSession) {
          pendDel += 1;
          deliverableList.push({ weekTitle: week.title, title: task.title, refId: task.refId });
        }
      }
    }
    return { pendingContent: pendCont, pendingDeliverables: pendDel, pendingDeliverableList: deliverableList };
  }, [weeklyData]);
  const [calPendingDeliverablesOpen, setCalPendingDeliverablesOpen] = useState(false);
  const [mySubmittedOpen, setMySubmittedOpen] = useState(false);

  // Compute dynamic event status
  const getEventStatus = (
    scheduledDate: string | null,
    startHour?: number | null,
    startMinute?: number | null,
    endHour?: number | null,
    endMinute?: number | null,
  ): 'completed' | 'live' | 'upcoming' => {
    if (!scheduledDate) return 'upcoming';
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (scheduledDate < today) return 'completed';
    if (scheduledDate > today) return 'upcoming';
    // Hoy: evaluar por hora (local)
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const start = (startHour ?? 0) * 60 + (startMinute ?? 0);
    const end = (endHour ?? 23) * 60 + (endMinute ?? 59);
    if (currentMinutes >= end) return 'completed';
    if (currentMinutes >= start) return 'live';
    return 'upcoming';
  };

  const getWeekForDate = (dateStr: string, baseDateStr: string | null | undefined): number => {
    if (!baseDateStr) return currentProgramWeek;
    const parseLocal = (str: string) => {
      const ymd = str.slice(0, 10).split('-');
      const d = ymd.length === 3 ? new Date(Number(ymd[0]), Number(ymd[1]) - 1, Number(ymd[2])) : new Date(str);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };
    const base = parseLocal(baseDateStr);
    const date = parseLocal(dateStr);
    const diffDays = Math.floor((date.getTime() - base.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 1;
    return Math.floor(diffDays / 7) + 1;
  };

  // Gather workshop ref_ids for useWorkshopTools
  const workshopRefIds = useMemo(() => {
    return (scheduleItems ?? [])
      .filter(item => item.type === 'workshop' && item.ref_id)
      .map(item => item.ref_id!)
      .filter(Boolean);
  }, [scheduleItems]);
  
  const { data: workshopToolsMap = {} } = useWorkshopTools(workshopRefIds);

  // Use real data if available
  const hasRealData = scheduleItems && scheduleItems.length > 0;
  const mappedRealEvents: CalendarEvent[] = (scheduleItems ?? []).map((item) => {
    const delInfo = item.ref_id ? submittedDeliverablesMap.get(item.ref_id) : undefined;
    return {
      id: item.id,
      title: item.title || 'Sin título',
      description: item.workshop?.description || item.mentorship?.description || '',
      type: (item.type as CalendarEventType) || 'workshop',
      date: item.scheduled_date || '',
      startTime: item.start_hour != null ? `${String(item.start_hour).padStart(2, '0')}:${String(item.start_minute ?? 0).padStart(2, '0')}` : '10:00',
      endTime: item.end_hour != null ? `${String(item.end_hour).padStart(2, '0')}:${String(item.end_minute ?? 0).padStart(2, '0')}` : '12:00',
      status: getEventStatus(item.scheduled_date, item.start_hour, item.start_minute, item.end_hour, item.end_minute),
      week: item.week_number || 1,
      meetLink: item.connection_link || undefined,
      recordingUrl: item.recording_link || undefined,
      meetRecordingUrl: item.meet_recording_link || undefined,
      materialUrl: item.workshop?.material_link || undefined,
      expert: item.expert ? {
        id: item.expert.id,
        name: item.expert.name,
        photo: item.expert.photo_url || '',
        profile: item.expert.profile || '',
        linkedinUrl: item.expert.linkedin_url || '',
      } : undefined,
      deliverableId: item.type === 'workshop' && item.ref_id && item.workshop?.requires_deliverable !== false ? item.ref_id : undefined,
      deliverableUploaded: item.type === 'workshop' && item.ref_id && item.workshop?.requires_deliverable !== false ? submittedWorkshopIds.has(item.ref_id) : false,
      deliverableStatus: delInfo?.status,
      deliverableFeedback: delInfo?.feedback,
      deliverableFileUrl: delInfo?.file_url ?? undefined,
      deliverableSubmittedAt: delInfo?.submitted_at ?? undefined,
      duration: item.workshop?.duration || item.mentorship?.duration || undefined,
      problemaConcreto: item.workshop?.problema_concreto || undefined,
      resultadoEsperado: item.workshop?.resultado_esperado || undefined,
      tiempoEstimado: item.workshop?.tiempo_estimado || undefined,
      tipAdicional: item.workshop?.tip_adicional || undefined,
      deliverableInstructions: undefined,
      deliverableTools: item.ref_id ? workshopToolsMap[item.ref_id] ?? undefined : undefined,
      deliverableResourceUrl: item.workshop?.deliverable_resource_url ?? null,
    };
  });
  
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('calendar_onboarding_seen');
  });
  const [typeFilter, setTypeFilter] = useState<EventTypeFilter>('all');
  const [weekFilter, setWeekFilter] = useState<WeekFilter>('all');
  const [expertDialogOpen, setExpertDialogOpen] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [selectedEventForInstructions, setSelectedEventForInstructions] = useState<CalendarEvent | null>(null);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [resourceHtml, setResourceHtml] = useState<string | null>(null);

  useEffect(() => {
    const url = selectedEventForInstructions?.deliverableResourceUrl;
    if (!resourceModalOpen || !url) {
      setResourceHtml(null);
      return;
    }
    let cancelled = false;
    setResourceHtml(null);
    fetch(url)
      .then((r) => r.text())
      .then((html) => { if (!cancelled) setResourceHtml(html); })
      .catch(() => { if (!cancelled) setResourceHtml('<p style="font-family:sans-serif;padding:24px">No se pudo cargar el recurso.</p>'); });
    return () => { cancelled = true; };
  }, [resourceModalOpen, selectedEventForInstructions?.deliverableResourceUrl]);
  const [expandedContentWeeks, setExpandedContentWeeks] = useState<Set<number>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [selectedToolId, setSelectedToolId] = useState<string>('');
  const { data: aiToolsCatalog = [] } = useAIToolsCatalog();
  const sortedAiTools = useMemo(
    () => [...aiToolsCatalog].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [aiToolsCatalog]
  );

  // Preload tool selection when opening modal for an existing deliverable
  useEffect(() => {
    if (!instructionsDialogOpen || !selectedEventForInstructions?.deliverableId) {
      return;
    }
    const existing = existingDeliverables.find(
      d => d.workshop_id === selectedEventForInstructions.deliverableId && d.user_id === user?.id
    );
    if (existing?.tool_used) {
      const match = aiToolsCatalog.find(t => t.name === existing.tool_used);
      setSelectedToolId(match?.id ?? '');
    } else {
      setSelectedToolId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructionsDialogOpen, selectedEventForInstructions?.deliverableId]);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [isSyncingAll, setIsSyncingAll] = useState(false);

  // Helper: convert a CalendarEvent to CalendarEventData for utils
  const toCalendarEventData = (e: CalendarEvent): CalendarEventData => ({
    title: e.title,
    description: e.description || '',
    date: e.date.split('T')[0],
    startTime: e.startTime,
    endTime: e.endTime,
    location: e.meetLink || undefined,
  });

  // Future events (upcoming or live) for bulk sync
  const futureEvents = useMemo(
    () => events.filter(e => e.status === 'upcoming' || e.status === 'live'),
    [events]
  );

  const handleAddToGoogleCalendar = (event: CalendarEvent) => {
    const url = buildGoogleCalendarUrl(toCalendarEventData(event));
    window.open(url, '_blank');
  };




  // Self-paced (perpetual) routes have no schedule: build the timeline from chapters
  const selfPacedWeekEvents: CalendarEvent[] = useMemo(() => {
    if (!isSelfPacedCohort) return [];
    const weeks = Array.from(
      new Set(chapters.map(ch => ch.week_number).filter((w): w is number => !!w))
    ).sort((a, b) => a - b);
    if (weeks.length === 0) return [];

    const planStartStr = activeEnrollment?.plan_start_at ?? null;
    const planStart = planStartStr
      ? new Date(
          Number(planStartStr.slice(0, 4)),
          Number(planStartStr.slice(5, 7)) - 1,
          Number(planStartStr.slice(8, 10))
        )
      : new Date();

    return weeks.map(weekNum => {
      const date = new Date(planStart);
      date.setDate(date.getDate() + (weekNum - 1) * 7);
      const dateStr = format(date, 'yyyy-MM-dd');
      const weekChapters = chapters.filter(ch => ch.week_number === weekNum);
      const title = weekChapters[0]?.name || `Semana ${weekNum}`;
      return {
        id: `self-paced-week-${weekNum}`,
        title,
        description: 'Contenido sugerido para esta semana de tu ruta de autoaprendizaje.',
        type: 'content' as CalendarEventType,
        date: dateStr,
        startTime: '00:00',
        endTime: '23:59',
        status: (weekNum < currentProgramWeek
          ? 'completed'
          : weekNum === currentProgramWeek
            ? 'live'
            : 'upcoming') as CalendarEvent['status'],
        week: weekNum,
      };
    });
  }, [isSelfPacedCohort, chapters, activeEnrollment?.plan_start_at, currentProgramWeek]);

  // Swap to real schedule data when available + inject special events
  useEffect(() => {
    if (isSelfPacedCohort) {
      setEvents(selfPacedWeekEvents);
      return;
    }
    if (hasRealData) {
      const allEvents = [...mappedRealEvents];
      
      // Inject project deadline event
      if (activeEnrollment?.project_deadline) {
        const baseDate = activeEnrollment.week_start_date || activeEnrollment.plan_start_at;
        const deadlineWeek = activeEnrollment.project_deadline_week ?? getWeekForDate(activeEnrollment.project_deadline, baseDate);
        allEvents.push({
          id: 'deadline-project',
          title: '📅 Fecha límite entrega de proyecto',
          description: 'Fecha máxima para entregar tu proyecto final.',
          type: 'deadline' as CalendarEventType,
          date: activeEnrollment.project_deadline,
          startTime: '23:59',
          endTime: '23:59',
          status: getEventStatus(activeEnrollment.project_deadline),
          week: deadlineWeek,
        });
      }

      // Inject Challenge Lab deadline event
      if (activeEnrollment?.challenge_lab_deadline) {
        const baseDate = activeEnrollment.week_start_date || activeEnrollment.plan_start_at;
        const deadlineWeek = activeEnrollment.challenge_lab_deadline_week ?? getWeekForDate(activeEnrollment.challenge_lab_deadline, baseDate);
        allEvents.push({
          id: 'deadline-challenge-lab',
          title: '🧪 Fecha límite Lab. Retos y Soluciones',
          description: 'Fecha máxima para completar el laboratorio de retos y soluciones.',
          type: 'deadline' as CalendarEventType,
          date: activeEnrollment.challenge_lab_deadline,
          startTime: '23:59',
          endTime: '23:59',
          status: getEventStatus(activeEnrollment.challenge_lab_deadline),
          week: deadlineWeek,
        });
      } else if (activeEnrollment?.challenge_lab_deadline_week && activeEnrollment?.week_start_date) {
        // Fecha derivada: inicio de semanas + semana (último día de esa semana)
        const startDate = new Date(activeEnrollment.week_start_date + 'T12:00:00');
        const derivedDate = new Date(startDate);
        derivedDate.setDate(derivedDate.getDate() + (activeEnrollment.challenge_lab_deadline_week - 1) * 7 + 6);
        const derivedDateStr = format(derivedDate, 'yyyy-MM-dd');
        allEvents.push({
          id: 'deadline-challenge-lab',
          title: '🧪 Fecha límite Lab. Retos y Soluciones',
          description: 'Fecha máxima para completar el laboratorio de retos y soluciones.',
          type: 'deadline' as CalendarEventType,
          date: derivedDateStr,
          startTime: '23:59',
          endTime: '23:59',
          status: getEventStatus(derivedDateStr),
          week: activeEnrollment.challenge_lab_deadline_week,
        });
      }
      
      // Inject Demo Day event
      if (activeEnrollment?.demo_day_enabled && activeEnrollment?.demo_day_date) {
        const baseDate = activeEnrollment.week_start_date || activeEnrollment.plan_start_at;
        const demoWeek = getWeekForDate(activeEnrollment.demo_day_date, baseDate);
        allEvents.push({
          id: 'demoday-event',
          title: '🎤 Demo Day',
          description: 'Presentación de proyectos finales ante el jurado.',
          type: 'demoday' as CalendarEventType,
          date: activeEnrollment.demo_day_date,
          startTime: '09:00',
          endTime: '13:00',
          status: getEventStatus(activeEnrollment.demo_day_date),
          week: demoWeek,
          meetLink: activeEnrollment.demo_day_link || undefined,
        });
      }
      
      setEvents(allEvents);
    }
  }, [isSelfPacedCohort, selfPacedWeekEvents, hasRealData, scheduleItems, submittedWorkshopIds, submittedDeliverablesMap, workshopToolsMap, activeEnrollment, currentProgramWeek]);

  // Deep-link: open a deliverable's instructions modal via ?openDeliverable=<workshopId>
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const targetId = searchParams.get('openDeliverable');
    if (!targetId || events.length === 0) return;
    const match = events.find(e => e.deliverableId === targetId);
    if (match) {
      setSelectedEventForInstructions(match);
      setInstructionsDialogOpen(true);
      searchParams.delete('openDeliverable');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, events, setSearchParams]);

  // Auto-expand current week's content
  useEffect(() => {
    if (currentProgramWeek > 0) {
      setExpandedContentWeeks(prev => {
        if (prev.has(currentProgramWeek)) return prev;
        const newSet = new Set(prev);
        newSet.add(currentProgramWeek);
        return newSet;
      });
    }
  }, [currentProgramWeek]);

  const toggleContentWeek = (week: number) => {
    setExpandedContentWeeks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(week)) {
        newSet.delete(week);
      } else {
        newSet.add(week);
      }
      return newSet;
    });
  };

  // Build real weekly content from chapters
  const getWeeklyContent = (weekNum: number) => {
    const weekChapters = chapters.filter(ch => ch.week_number === weekNum);
    if (weekChapters.length === 0) return null;
    
    const allContents = weekChapters.flatMap(ch => 
      ch.contents.filter(c => c.is_mandatory).map(c => ({
        id: c.content.id,
        title: c.content.name,
        duration: c.content.duration || '',
        type: mapContentType(c.content.content_type),
        playerType: mapPlayerContentType((c.content as any).content_type),
        description: (c.content as any).description || '',
        thumbnail: (c.content as any).thumbnail_url || '',
        thumbnail_url: (c.content as any).thumbnail_url || null,
        vimeo_player_url: (c.content as any).vimeo_player_url ?? null,
        html_content: (c.content as any).html_content ?? null,
        url: (c.content as any).url ?? null,
        completed: completedContentIds.has(c.content.id),
        progress: progressMap.get(c.content.id) ?? 0,
      }))
    );
    
    if (allContents.length === 0) return null;
    
    return {
      week: weekNum,
      lessons: allContents,
    };
  };

  const mapContentType = (contentType: string | null): 'video' | 'reading' | 'interactive' | 'lia-class' => {
    if (!contentType) return 'reading';
    const lower = contentType.toLowerCase();
    if (lower.includes('video') || lower.includes('clase con lia')) return 'video';
    if (lower.includes('interactivo') || lower.includes('interactive')) return 'interactive';
    if (lower.includes('lia') || lower.includes('clase')) return 'lia-class';
    return 'reading';
  };

  // Map DB content_type to the player's content type (same rules as /content)
  const mapPlayerContentType = (dbType: string | null): PlayerContentType => {
    switch (dbType) {
      case 'video':
      case 'Video': return 'video';
      case 'interactive':
      case 'Interactivo':
      case 'ova': return 'interactive';
      case 'reading':
      case 'Lectura':
      case 'article': return 'reading';
      case 'Recurso': return 'resource';
      case 'infographic':
      case 'Infografía':
      case 'audio':
      case 'Audio':
      case 'podcast': return 'infographic';
      case 'lia-class':
      case 'Clase con LIA': return 'lia-class';
      default: return 'reading';
    }
  };

  const getWeeklyContentProgress = (weekNum: number) => {
    const weekContent = getWeeklyContent(weekNum);
    if (!weekContent) return { completed: 0, total: 0, percentage: 0 };
    
    const total = weekContent.lessons.length;
    const completed = weekContent.lessons.filter(l => l.completed).length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { completed, total, percentage };
  };

  const getLessonTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <PlayCircle className="w-4 h-4" />;
      case 'interactive': return <BookOpen className="w-4 h-4" />;
      case 'lia-class': return <Users className="w-4 h-4" />;
      case 'reading': return <FileText className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('calendar_onboarding_seen', 'true');
    setShowOnboarding(false);
  };

  // Filtered events based on type and week filters
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const matchesType = typeFilter === 'all' || event.type === typeFilter;
      const matchesWeek = weekFilter === 'all' || event.week === weekFilter;
      return matchesType && matchesWeek;
    });
  }, [events, typeFilter, weekFilter]);

  // Group events by date and type for calendar coloring
  const eventsByDateAndType = useMemo(() => {
    const map = new Map<string, Set<CalendarEventType>>();
    filteredEvents.forEach(event => {
      const dateKey = event.date.split('T')[0];
      if (!map.has(dateKey)) {
        map.set(dateKey, new Set());
      }
      map.get(dateKey)!.add(event.type);
    });
    return map;
  }, [filteredEvents]);

  // Create date arrays for each event type — fix timezone with T00:00:00
  const safeParse = (dateStr: string) => new Date(dateStr.split('T')[0] + 'T00:00:00');
  const workshopDates = filteredEvents.filter(e => e.type === 'workshop').map(e => safeParse(e.date));
  const mentoringDates = filteredEvents.filter(e => e.type === 'mentoring').map(e => safeParse(e.date));
  const contentDates = filteredEvents.filter(e => e.type === 'content').map(e => safeParse(e.date));
  const projectDeliveryDates = filteredEvents.filter(e => e.type === 'project_delivery').map(e => safeParse(e.date));
  // Split deadlines: project vs challenge lab (by id) so we can color them differently
  const projectDeadlineDates = filteredEvents.filter(e => e.type === 'deadline' && e.id === 'deadline-project').map(e => safeParse(e.date));
  const challengeLabDeadlineDates = filteredEvents.filter(e => e.type === 'deadline' && e.id === 'deadline-challenge-lab').map(e => safeParse(e.date));
  const demodayDates = filteredEvents.filter(e => e.type === 'demoday').map(e => safeParse(e.date));

  const eventDates = filteredEvents.map(event => safeParse(event.date));

  const eventsForSelectedDate = selectedDate 
    ? filteredEvents.filter(event => isSameDay(safeParse(event.date), selectedDate))
    : [];

  // Eventos sintéticos de rutas de autoaprendizaje: solo sirven como fuente de
  // datos (puntos del mes, agrupación, fechas sugeridas); no se renderizan como tarjeta-evento
  const isSyntheticWeekEvent = (event: CalendarEvent) => event.id.startsWith('self-paced-week-');

  // Rango sugerido (inicio + 6 días) de una semana de autoaprendizaje
  const getSelfPacedWeekRangeLabel = (weekNum: number): string | null => {
    const synthetic = selfPacedWeekEvents.find(e => e.week === weekNum);
    if (!synthetic) return null;
    const start = safeParse(synthetic.date);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `Sugerida: del ${format(start, "d 'de' MMMM", { locale: es })} al ${format(end, "d 'de' MMMM", { locale: es })}`;
  };

  const getStatusBadge = (status: CalendarEvent['status']) => {
    switch (status) {
      case 'live':
        return <Badge className="bg-red-500 text-white animate-pulse">EN VIVO</Badge>;
      case 'completed':
        return <Badge variant="secondary">Completado</Badge>;
      case 'upcoming':
        return <Badge variant="outline">Próximo</Badge>;
    }
  };

  const getTypeBadge = (type: CalendarEvent['type']) => {
    switch (type) {
      case 'workshop':
        return <Badge className="bg-primary/20 text-primary"><Users className="w-3 h-3 mr-1" />Sesión en Vivo</Badge>;
      case 'mentoring':
        return <Badge className="bg-violet-500/20 text-violet-700 dark:text-violet-300"><User className="w-3 h-3 mr-1" />Mentoría</Badge>;
      case 'content':
        return <Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-300"><BookOpen className="w-3 h-3 mr-1" />Contenido</Badge>;
      case 'project_delivery':
        return <Badge className="bg-success/20 text-success"><Award className="w-3 h-3 mr-1" />Entrega</Badge>;
      case 'deadline':
        return <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-300"><AlertCircle className="w-3 h-3 mr-1" />Fecha límite</Badge>;
      case 'demoday':
        return <Badge className="bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"><Award className="w-3 h-3 mr-1" />Demo Day</Badge>;
    }
  };

  // Normaliza URLs externas para que no se interpreten como rutas relativas
  // cuando vienen sin protocolo (ej. "meet.google.com/abc-defg-hij")
  const normalizeExternalUrl = (url: string): string => {
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed.replace(/^\/+/, '')}`;
  };

  const handleJoinMeet = (meetLink: string) => {
    window.open(normalizeExternalUrl(meetLink), '_blank');
  };

  const handleWatchRecording = (recordingUrl: string) => {
    window.open(normalizeExternalUrl(recordingUrl), '_blank');
  };

  const handleOpenMaterial = (materialUrl: string) => {
    window.open(materialUrl, '_blank');
  };

  const handleOpenExpert = (expert: Expert) => {
    setSelectedExpert(expert);
    setExpertDialogOpen(true);
  };

  const handleOpenInstructions = (event: CalendarEvent) => {
    setSelectedEventForInstructions(event);
    setInstructionsDialogOpen(true);
    setIsReplacing(false);
  };

  // Submit deliverable with multi-file upload support + optional comment
  const handleSubmitDeliverable = async () => {
    if (!selectedEventForInstructions || !selectedEventForInstructions.deliverableId || !user) return;

    const fileInput = document.getElementById('deliverable-file-modal') as HTMLInputElement;
    const linkInput = document.getElementById('deliverable-link-modal') as HTMLInputElement;
    const commentInput = document.getElementById('deliverable-comment-modal') as HTMLTextAreaElement;
    const linkValue = linkInput?.value?.trim() || '';
    const commentValue = commentInput?.value?.trim() || '';
    const files = fileInput?.files ? Array.from(fileInput.files) : [];

    if (files.length === 0 && !linkValue) {
      toast.error('Debes subir al menos un archivo o ingresar un enlace');
      return;
    }

    // Validate the entire selection before uploading any file.
    for (const file of files) {
      const check = validateDeliverableUpload(file);
      if (!check.ok) {
        toast.error(`${file.name}: ${check.error}`);
        return;
      }
    }

    setIsUploading(true);
    const uploadedUrls: string[] = [];

    try {
      // Upload all selected files
      for (const file of files) {
        const filePath = `${user.id}/${selectedEventForInstructions.deliverableId}/${Date.now()}-${sanitizeFilename(file.name)}`;
        const { error: uploadError } = await supabase.storage
          .from('deliverables')
          .upload(filePath, file, { upsert: true, contentType: safeContentType(file) });

        if (uploadError) throw uploadError;

        const { data: signedData } = await supabase.storage
          .from('deliverables')
          .createSignedUrl(filePath, 60 * 60 * 24 * 365);

        uploadedUrls.push(signedData?.signedUrl || filePath);
      }

      if (linkValue) uploadedUrls.push(linkValue);

      const primaryUrl = uploadedUrls[0];

      // Check if deliverable already exists (replacing)
      const existingDeliverable = existingDeliverables.find(
        d => d.workshop_id === selectedEventForInstructions.deliverableId && d.user_id === user.id
      );

      const selectedToolName = sortedAiTools.find(t => t.id === selectedToolId)?.name ?? null;

      if (existingDeliverable) {
        const { error: updateError } = await supabase
          .from('participant_deliverables')
          .update({
            file_url: primaryUrl,
            file_urls: uploadedUrls,
            comment: commentValue || null,
            tool_used: selectedToolName,
            submitted_at: new Date().toISOString(),
            status: 'submitted' as const,
          })
          .eq('id', existingDeliverable.id);

        if (updateError) throw updateError;

        toast.success('Entregable actualizado correctamente');
        setInstructionsDialogOpen(false);
        setSelectedEventForInstructions(null);
        setIsUploading(false);
        setIsReplacing(false);
        setSelectedToolId('');
        queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      } else {
        createDeliverable.mutate({
          name: `Entregable - ${selectedEventForInstructions.title}`,
          workshop_id: selectedEventForInstructions.deliverableId,
          cohort_id: selectedCohortId || undefined,
          file_url: primaryUrl,
          file_urls: uploadedUrls,
          comment: commentValue || undefined,
          tool_used: selectedToolName,
        }, {
          onSuccess: () => {
            toast.success('Entregable subido correctamente');
            setInstructionsDialogOpen(false);
            setSelectedEventForInstructions(null);
            setIsUploading(false);
            setIsReplacing(false);
            setSelectedToolId('');
          },
          onError: () => {
            toast.error('Error al subir el entregable');
            setIsUploading(false);
          },
        });
      }
    } catch (err) {
      toast.error('Error al subir el archivo');
      setIsUploading(false);
    }
  };

  // Events for current week
  const currentWeekEvents = filteredEvents.filter(e => e.week === currentProgramWeek);

  // Past and upcoming events based on week
  const pastEvents = filteredEvents.filter(e => e.week < currentProgramWeek);
  const upcomingEvents = filteredEvents.filter(e => e.week > currentProgramWeek);

  // Group events by week for timeline view
  const groupEventsByWeek = (events: CalendarEvent[]) => {
    const grouped = events.reduce((acc, event) => {
      const week = event.week;
      if (!acc[week]) {
        acc[week] = [];
      }
      acc[week].push(event);
      return acc;
    }, {} as Record<number, CalendarEvent[]>);
    
    return Object.entries(grouped)
      .map(([week, events]) => ({ week: Number(week), events }))
      .sort((a, b) => a.week - b.week);
  };

  const pastEventsByWeek = groupEventsByWeek(pastEvents);
  const upcomingEventsByWeek = groupEventsByWeek(upcomingEvents);

  // Render content card for a week
  const renderWeekContentCard = (weekNum: number) => {
    const weekContent = getWeeklyContent(weekNum);
    if (!weekContent) return null;
    
    const contentProgress = getWeeklyContentProgress(weekNum);
    const isContentExpanded = expandedContentWeeks.has(weekNum);
    
    return (
      <div className="relative">
        <div className="absolute -left-[1.4rem] top-4 w-3 h-3 rounded-full bg-blue-500 border-2 border-background" />
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
          <Collapsible open={isContentExpanded} onOpenChange={() => toggleContentWeek(weekNum)}>
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-2 cursor-pointer hover:bg-blue-100/50 dark:hover:bg-blue-900/20 transition-colors rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        Contenido Obligatorio
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {contentProgress.completed}/{contentProgress.total} lecciones completadas
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right hidden sm:block">
                      <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {contentProgress.percentage}%
                      </span>
                    </div>
                    <ChevronDown className={cn(
                      "w-5 h-5 text-muted-foreground transition-transform duration-200",
                      isContentExpanded && "rotate-180"
                    )} />
                  </div>
                </div>
                <div className="mt-3">
                  <Progress value={contentProgress.percentage} className="h-2" />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="pt-0 pb-4">
                <div className="space-y-2 mt-2">
                  {weekContent.lessons.map((lesson, lessonIdx) => (
                    <div 
                      key={lesson.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-100/70 dark:hover:bg-blue-900/30 transition-colors cursor-pointer group"
                      onClick={() => handleOpenLessonInCalendar(lesson, lessonIdx)}
                    >
                      <div className="shrink-0">
                        {lesson.completed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : lesson.progress > 0 ? (
                          <div className="relative">
                            <svg className="w-5 h-5 -rotate-90">
                              <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted/30" />
                              <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray={`${lesson.progress * 0.5} 100`} className="text-blue-500" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                        )}
                      </div>
                      <div className="p-1.5 rounded bg-muted/50 shrink-0">
                        {getLessonTypeIcon(lesson.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm font-medium truncate", lesson.completed && "text-muted-foreground line-through")}>
                          {lesson.title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {lesson.duration} • {lesson.type === 'lia-class' ? 'Clase LIA' : lesson.type === 'interactive' ? 'Interactivo' : lesson.type === 'reading' ? 'Lectura' : 'Video'}
                        </p>
                      </div>
                      {lesson.progress > 0 && !lesson.completed && (
                        <Badge variant="secondary" className="text-xs shrink-0">{lesson.progress}%</Badge>
                      )}
                      <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </div>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  onClick={() => navigate(`/content?week=${weekNum}`)}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Ver todo el contenido de la semana
                </Button>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      </div>
    );
  };

  // Render grouped events with timeline
  const renderWeekTimeline = (weekGroups: { week: number; events: CalendarEvent[] }[], reverse = false) => {
    const sortedGroups = reverse ? [...weekGroups].reverse() : weekGroups;
    
    return (
      <div className="space-y-6">
        {sortedGroups.map((group) => {
          const weekContent = getWeeklyContent(group.week);
          const contentProgress = getWeeklyContentProgress(group.week);
          // En autoaprendizaje los eventos sintéticos no se muestran como tarjeta: solo el contenido
          const visibleEvents = isSelfPacedCohort
            ? group.events.filter(e => !isSyntheticWeekEvent(e))
            : group.events;
          const selfPacedRange = isSelfPacedCohort ? getSelfPacedWeekRangeLabel(group.week) : null;

          return (
            <div key={group.week} className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold text-sm shrink-0">
                  {group.week === 999 ? '🏁' : group.week}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">{group.week === 999 ? 'Cierre de la ruta' : `Semana ${group.week}`}</h3>
                  <p className="text-xs text-muted-foreground">
                    {isSelfPacedCohort ? (
                      <>
                        {weekContent ? `${weekContent.lessons.length} lecciones` : 'Sin lecciones'}
                        {selfPacedRange && ` • ${selfPacedRange}`}
                      </>
                    ) : (
                      <>
                        {group.events.length} {group.events.length === 1 ? 'evento' : 'eventos'}
                        {weekContent && ` • ${weekContent.lessons.length} lecciones`}
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="pl-5 border-l-2 border-muted ml-5 space-y-4">
                {renderWeekContentCard(group.week)}

                {visibleEvents.map(event => (
                  <div key={event.id} className="relative">
                    <div className="absolute -left-[1.4rem] top-4 w-3 h-3 rounded-full bg-muted-foreground/30 border-2 border-background" />
                    {renderEventCard(event)}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderEventCard = (event: CalendarEvent) => (
    <Card key={event.id} className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2 flex-wrap">
              {getTypeBadge(event.type)}
              {getStatusBadge(event.status)}
              <Badge variant="outline" className="text-xs">{event.week === 999 ? 'Cierre' : `Semana ${event.week}`}</Badge>
              {event.duration && (
                <Badge variant="outline" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />{event.duration}
                </Badge>
              )}
            </div>
            <CardTitle className="text-lg">{event.title}</CardTitle>
          </div>
        </div>
        {event.description && <CardDescription>{event.description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Problema concreto y resultado esperado */}
          {(event.problemaConcreto || event.resultadoEsperado) && (
            <div className="space-y-2">
              {event.problemaConcreto && (
                <div className="flex items-start gap-2 text-sm p-2 rounded-lg bg-destructive/5 border border-destructive/10">
                  <span className="shrink-0">🎯</span>
                  <div>
                    <span className="font-medium text-destructive">Problema:</span>{' '}
                    <span className="text-muted-foreground">{event.problemaConcreto}</span>
                  </div>
                </div>
              )}
              {event.resultadoEsperado && (
                <div className="flex items-start gap-2 text-sm p-2 rounded-lg bg-success/5 border border-success/10">
                  <span className="shrink-0">✅</span>
                  <div>
                    <span className="font-medium text-success">Resultado:</span>{' '}
                    <span className="text-muted-foreground">{event.resultadoEsperado}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarIcon className="w-4 h-4" />
            <span>
              {event.date && format(safeParse(event.date), "EEEE d 'de' MMMM, yyyy", { locale: es })}
            </span>
            <span className="font-medium">
              {event.startTime} - {event.endTime}
            </span>
          </div>

          {event.mentor && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span>Mentor: {event.mentor}</span>
            </div>
          )}

          {event.expert && (
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">Experto:</span>
              <button
                onClick={() => handleOpenExpert(event.expert!)}
                className="text-primary hover:underline font-medium"
              >
                {event.expert.name}
              </button>
            </div>
          )}

          {/* Indicador de requiere entregable (no subido aún) */}
          {event.deliverableId && !event.deliverableUploaded && (
            <div className="flex items-center gap-2 text-sm text-warning">
              <ClipboardList className="w-4 h-4" />
              <span className="font-medium">Requiere entregable</span>
            </div>
          )}

          {/* Feedback del mentor si requiere ajustes */}
          {event.deliverableStatus === 'needs_revision' && event.deliverableFeedback && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-lg bg-warning/10 border border-warning/20">
              <AlertCircle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-warning">Requiere ajustes</p>
                <p className="text-muted-foreground mt-1 italic">"{event.deliverableFeedback}"</p>
              </div>
            </div>
          )}

          {event.status === 'completed' && event.meetRecordingUrl && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={() => handleWatchRecording(event.meetRecordingUrl!)} variant="outline" size="sm" className="text-xs">
                <Play className="w-3 h-3 mr-1" />Grabación Meet
              </Button>
              {/* Grabación Fireflies oculta — solo se muestra la nativa de Meet */}
            </div>
          )}
          {event.type === 'workshop' && event.materialUrl && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={() => handleOpenMaterial(event.materialUrl!)} variant="outline" size="sm" className="text-xs">
                <Link2 className="w-3 h-3 mr-1" />Material
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            {event.meetLink && (event.status === 'live' || event.status === 'upcoming') && (
              <Button 
                onClick={() => handleJoinMeet(event.meetLink!)}
                variant={event.status === 'live' ? 'default' : 'outline'}
                size="sm"
              >
                <Video className="w-4 h-4 mr-2" />
                {event.status === 'live' ? 'Unirse ahora' : 'Link de Meet'}
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            )}

            {event.deliverableId && (
              event.deliverableUploaded ? (
                event.deliverableStatus === 'needs_revision' ? (
                  <Button 
                    onClick={() => handleOpenInstructions(event)}
                    variant="outline" 
                    size="sm"
                    className="border-warning text-warning hover:bg-warning/10"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Requiere ajustes
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="bg-success/10 text-success border-success/30 hover:bg-success/20" onClick={() => handleOpenInstructions(event)}>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Entregado
                  </Button>
                )
              ) : (
                <Button 
                  onClick={() => handleOpenInstructions(event)}
                  variant="secondary"
                  size="sm"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Entregable
                </Button>
              )
            )}

            {/* Botón de estado para eventos de Laboratorio de Retos y Proyecto Final */}
            {event.id === 'deadline-challenge-lab' && (
              calChallengeLabData?.completed ? (
                <Button variant="outline" size="sm" disabled className="bg-success/10 text-success border-success/30 cursor-default">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Completado
                </Button>
              ) : (
                <Button size="sm" onClick={() => navigate('/challenge-lab')}>
                  <Beaker className="w-4 h-4 mr-2" />
                  Pendiente — Ir al Laboratorio
                </Button>
              )
            )}
            {event.id === 'deadline-project' && (
              calProjectData?.completed ? (
                <Button variant="outline" size="sm" disabled className="bg-success/10 text-success border-success/30 cursor-default">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Completado
                </Button>
              ) : (
                <Button size="sm" onClick={() => navigate('/challenge')}>
                  <Briefcase className="w-4 h-4 mr-2" />
                  Pendiente — Ir al Proyecto
                </Button>
              )
            )}

            {(event.status === 'upcoming' || event.status === 'live') && (
              <Button
                onClick={() => handleAddToGoogleCalendar(event)}
                variant="ghost"
                size="sm"
                title="Agregar a Google Calendar"
              >
                <CalendarIcon className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline text-xs">Google Cal</span>
              </Button>
            )}

            {event.deliverableTools && event.deliverableTools.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <Wrench className="w-3 h-3 text-muted-foreground" />
                {event.deliverableTools.slice(0, 3).map(tool => (
                  <Badge key={tool} variant="outline" className="text-[10px] px-1.5 py-0">{tool}</Badge>
                ))}
                {event.deliverableTools.length > 3 && (
                  <span className="text-[10px] text-muted-foreground">+{event.deliverableTools.length - 3}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (showOnboarding) {
    return (
      <ModuleOnboarding
        onComplete={handleOnboardingComplete}
        steps={calendarOnboardingSteps}
        benefits={calendarOnboardingBenefits}
        ctaText="Ver Mi Calendario"
      />
    );
  }

  return (
    <div className="space-y-6">
      {enrollments.length > 1 && (
        <CertificationHeader enrollments={enrollments} selectedCohortId={selectedCohortId} onCohortChange={setSelectedCohortId} />
      )}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">Calendario</h1>
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground" onClick={() => setShowOnboarding(true)} title="Ver ayuda">
              <HelpCircle className="w-5 h-5" />
            </Button>
          </div>
          {!isSelfPacedCohort && <p className="text-muted-foreground mt-1">Sesiones en vivo y mentorías de la ruta</p>}
        </div>
        
        <Button 
          variant="outline" 
          className="gap-2"
          onClick={() => setSyncDialogOpen(true)}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.316 5.684H5.684v12.632h12.632V5.684z" fill="#fff"/>
            <path d="M18.316 22.105L22.105 18.316V5.684L18.316 1.895H5.684L1.895 5.684v12.632l3.789 3.789h12.632z" fill="#4285F4"/>
            <path d="M13.895 12L12 13.895 10.105 12 12 10.105 13.895 12z" fill="#EA4335"/>
            <path d="M18.316 5.684L12 12l6.316 6.316V5.684z" fill="#34A853"/>
            <path d="M5.684 5.684L12 12l-6.316 6.316V5.684z" fill="#FBBC04"/>
            <path d="M18.316 5.684H5.684L12 12l6.316-6.316z" fill="#EA4335"/>
            <path d="M5.684 18.316h12.632L12 12l-6.316 6.316z" fill="#188038"/>
          </svg>
          <span className="hidden sm:inline">Sincronizar con Google Calendar</span>
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 order-2 lg:order-1">
          {selectedDate && (
            <div className="flex items-center justify-between mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                <span className="font-medium text-foreground">
                  {format(selectedDate, "EEEE d 'de' MMMM, yyyy", { locale: es })}
                </span>
                <Badge variant="secondary" className="ml-2">
                  {eventsForSelectedDate.length} {eventsForSelectedDate.length === 1 ? 'evento' : 'eventos'}
                </Badge>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedDate(undefined)} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {selectedDate ? (
            <div className="space-y-4">
              {isSelfPacedCohort ? (
                (() => {
                  const weekEvent = eventsForSelectedDate.find(isSyntheticWeekEvent);
                  if (!weekEvent) {
                    return <Card><CardContent className="py-8 text-center text-muted-foreground">No hay contenido sugerido para este día</CardContent></Card>;
                  }
                  const rangeLabel = getSelfPacedWeekRangeLabel(weekEvent.week);
                  return (
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-semibold text-foreground">Semana {weekEvent.week} de tu ruta</h3>
                        {rangeLabel && <p className="text-xs text-muted-foreground">{rangeLabel}</p>}
                      </div>
                      {renderWeekContentCard(weekEvent.week) ?? (
                        <Card><CardContent className="py-8 text-center text-muted-foreground">Esta semana no tiene contenido obligatorio</CardContent></Card>
                      )}
                    </div>
                  );
                })()
              ) : eventsForSelectedDate.length === 0 ? (
                <Card><CardContent className="py-8 text-center text-muted-foreground">No hay eventos programados para este día</CardContent></Card>
              ) : (
                eventsForSelectedDate.map(renderEventCard)
              )}
            </div>
          ) : (
            <Tabs defaultValue="current" className="w-full">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <TabsList>
                  <TabsTrigger value="past">
                    <span className="hidden sm:inline">Semanas Anteriores</span>
                    <span className="sm:hidden">Anteriores</span>
                    <span className="ml-1">({isSelfPacedCohort ? pastEventsByWeek.length : pastEvents.length})</span>
                  </TabsTrigger>
                  <TabsTrigger value="current">
                    <span className="hidden sm:inline">Semana Actual</span>
                    <span className="sm:hidden">Actual</span>
                    <span className="ml-1">({isSelfPacedCohort ? (getWeeklyContent(currentProgramWeek) ? 1 : 0) : currentWeekEvents.length})</span>
                  </TabsTrigger>
                  <TabsTrigger value="upcoming">
                    <span className="hidden sm:inline">Próximas Semanas</span>
                    <span className="sm:hidden">Próximas</span>
                    <span className="ml-1">({isSelfPacedCohort ? upcomingEventsByWeek.length : upcomingEvents.length})</span>
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="past" className="mt-0">
                {pastEvents.length === 0 ? (
                  <Card><CardContent className="py-8 text-center text-muted-foreground">No hay eventos en semanas anteriores</CardContent></Card>
                ) : (
                  renderWeekTimeline(pastEventsByWeek, true)
                )}
              </TabsContent>

              <TabsContent value="current" className="mt-0">
                <div className="space-y-4">
                  {/* Real content card for current week */}
                  {(() => {
                    const weekContent = getWeeklyContent(currentProgramWeek);
                    if (!weekContent) return null;
                    const contentProg = getWeeklyContentProgress(currentProgramWeek);
                    return (
                      <Card className="border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
                        <Collapsible 
                          open={expandedContentWeeks.has(currentProgramWeek)} 
                          onOpenChange={() => toggleContentWeek(currentProgramWeek)}
                        >
                          <CollapsibleTrigger asChild>
                            <CardHeader className="cursor-pointer hover:bg-blue-100/50 dark:hover:bg-blue-900/20 transition-colors rounded-t-lg">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                                    <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                  </div>
                                  <div>
                                    <CardTitle className="text-base flex items-center gap-2">
                                      Contenido Obligatorio
                                      <Badge variant="secondary" className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                        Semana {currentProgramWeek}
                                      </Badge>
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                      {weekContent.lessons.length} lecciones
                                    </CardDescription>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Badge 
                                    variant={contentProg.percentage === 100 ? "default" : "secondary"}
                                    className={contentProg.percentage === 100 ? "bg-green-500" : ""}
                                  >
                                    {contentProg.completed}/{contentProg.total}
                                  </Badge>
                                  <ChevronDown className={cn(
                                    "w-5 h-5 text-muted-foreground transition-transform",
                                    expandedContentWeeks.has(currentProgramWeek) && "rotate-180"
                                  )} />
                                </div>
                              </div>
                              <div className="mt-3">
                                <Progress value={contentProg.percentage} className="h-2" />
                              </div>
                            </CardHeader>
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent>
                            <CardContent className="pt-0 pb-4">
                              <div className="space-y-2 mt-2">
                                {weekContent.lessons.map((lesson, lessonIdx) => (
                                  <div 
                                    key={lesson.id}
                                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-100/70 dark:hover:bg-blue-900/30 transition-colors cursor-pointer group"
                                    onClick={() => handleOpenLessonInCalendar(lesson, lessonIdx)}
                                  >
                                    <div className="shrink-0">
                                      {lesson.completed ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                      ) : lesson.progress > 0 ? (
                                        <div className="relative">
                                          <svg className="w-5 h-5 -rotate-90">
                                            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted/30" />
                                            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray={`${lesson.progress * 0.5} 100`} className="text-blue-500" />
                                          </svg>
                                        </div>
                                      ) : (
                                        <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                                      )}
                                    </div>
                                    <div className="p-1.5 rounded bg-muted/50 shrink-0">
                                      {getLessonTypeIcon(lesson.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={cn("text-sm font-medium truncate", lesson.completed && "text-muted-foreground line-through")}>
                                        {lesson.title}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {lesson.duration} • {lesson.type === 'lia-class' ? 'Clase LIA' : lesson.type === 'interactive' ? 'Interactivo' : lesson.type === 'reading' ? 'Lectura' : 'Video'}
                                      </p>
                                    </div>
                                    {lesson.progress > 0 && !lesson.completed && (
                                      <Badge variant="secondary" className="text-xs shrink-0">{lesson.progress}%</Badge>
                                    )}
                                    <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                  </div>
                                ))}
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="w-full mt-3 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                onClick={() => navigate(`/content?week=${currentProgramWeek}`)}
                              >
                                <BookOpen className="w-4 h-4 mr-2" />
                                Ver todo el contenido de la semana
                              </Button>
                            </CardContent>
                          </CollapsibleContent>
                        </Collapsible>
                      </Card>
                    );
                  })()}
                  
                  {(() => {
                    const visibleCurrentEvents = isSelfPacedCohort
                      ? currentWeekEvents.filter(e => !isSyntheticWeekEvent(e))
                      : currentWeekEvents;
                    if (visibleCurrentEvents.length === 0 && !getWeeklyContent(currentProgramWeek)) {
                      return <Card><CardContent className="py-8 text-center text-muted-foreground">No hay eventos en la semana actual</CardContent></Card>;
                    }
                    return visibleCurrentEvents.map(renderEventCard);
                  })()}
                </div>
              </TabsContent>


              <TabsContent value="upcoming" className="mt-0">
                {upcomingEvents.length === 0 ? (
                  <Card><CardContent className="py-8 text-center text-muted-foreground">No hay eventos en próximas semanas</CardContent></Card>
                ) : (
                  renderWeekTimeline(upcomingEventsByWeek)
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        <Card className="lg:col-span-1 order-1 lg:order-2 h-fit">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Selecciona una fecha
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Tipo de evento</Label>
                <Select value={typeFilter} onValueChange={(value: EventTypeFilter) => setTypeFilter(value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filtrar por tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="workshop">
                      <span className="flex items-center gap-2"><Users className="w-3 h-3" /> Sesión en Vivo</span>
                    </SelectItem>
                    <SelectItem value="mentoring">
                      <span className="flex items-center gap-2"><User className="w-3 h-3" /> Mentoría</span>
                    </SelectItem>
                    <SelectItem value="project_delivery">
                      <span className="flex items-center gap-2"><Award className="w-3 h-3" /> Entrega</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Semana de la ruta</Label>
                <Select value={String(weekFilter)} onValueChange={(value) => setWeekFilter(value === 'all' ? 'all' : Number(value))}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filtrar por semana" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las semanas</SelectItem>
                    {availableWeeks.map(week => {
                      const isCurrentWeek = week === currentProgramWeek;
                      return (
                        <SelectItem key={week} value={String(week)}>
                          <span className="flex items-center gap-2">
                            Semana {week}
                            {isCurrentWeek && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-primary/20 text-primary">
                                Actual
                              </Badge>
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <span>Sesión en Vivo</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-violet-500" />
                  <span>Mentoría</span>
                </div>
              <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span>Entrega de Proyecto</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-pink-500" />
                  <span>Lab. Retos</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span>Demo Day</span>
                </div>
              </div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                locale={es}
                className="rounded-md border pointer-events-auto scale-95 origin-top"
                modifiers={{
                  workshop: workshopDates,
                  mentoring: mentoringDates,
                  projectDeadline: projectDeadlineDates,
                  challengeLabDeadline: challengeLabDeadlineDates,
                  demoday: demodayDates,
                }}
                modifiersStyles={{
                  workshop: {
                    fontWeight: 'bold',
                    backgroundColor: 'hsl(var(--primary) / 0.2)',
                    color: 'hsl(var(--primary))',
                    borderRadius: '50%',
                  },
                  mentoring: {
                    fontWeight: 'bold',
                    backgroundColor: 'rgb(139 92 246 / 0.2)',
                    color: 'rgb(139 92 246)',
                    borderRadius: '50%',
                  },
                  projectDeadline: {
                    fontWeight: 'bold',
                    backgroundColor: 'rgb(245 158 11 / 0.2)',
                    color: 'rgb(245 158 11)',
                    borderRadius: '50%',
                  },
                  challengeLabDeadline: {
                    fontWeight: 'bold',
                    backgroundColor: 'rgb(236 72 153 / 0.2)',
                    color: 'rgb(236 72 153)',
                    borderRadius: '50%',
                  },
                  demoday: {
                    fontWeight: 'bold',
                    backgroundColor: 'rgb(16 185 129 / 0.2)',
                    color: 'rgb(16 185 129)',
                    borderRadius: '50%',
                  },
                }}
              />
            </div>

            {/* Pendientes Widget — componente compartido con /dashboard */}
            <div className="border-t pt-4">
              <PendingItemsWidget
                variant="compact"
                data={{
                  pendingContent,
                  pendingDeliverables,
                  hasSubmittedProject: calProjectData?.completed ?? false,
                  challengeLabCompleted: calChallengeLabData?.completed ?? false,
                  selfPaced: !!activeEnrollment?.is_perpetual,
                }}
                onShowSection={(section) => {
                  if (section === 'content') {
                    navigate('/content?mandatory=1');
                  } else {
                    setCalPendingDeliverablesOpen(true);
                  }
                }}
              />
              <Dialog open={calPendingDeliverablesOpen} onOpenChange={setCalPendingDeliverablesOpen}>
                <DialogContent className="max-w-lg max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle>Entregables pendientes ({pendingDeliverableList.length})</DialogTitle>
                  </DialogHeader>
                  {pendingDeliverableList.length === 0 ? (
                    <p className="text-sm text-success">✓ Todos los entregables enviados</p>
                  ) : (
                    <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-2">
                      {pendingDeliverableList.map((item, i) => (
                        <button
                          key={i}
                          type="button"
                          disabled={!item.refId}
                          onClick={() => {
                            setCalPendingDeliverablesOpen(false);
                            if (item.refId) {
                              setSearchParams({ openDeliverable: item.refId }, { replace: true });
                            }
                          }}
                          className="w-full flex items-start gap-3 p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left"
                        >
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-warning shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                            <p className="text-xs text-muted-foreground">Semana: {item.weekTitle}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                size="sm"
                className="w-full mt-3 gap-2"
                onClick={() => setMySubmittedOpen(true)}
              >
                <ClipboardList className="w-4 h-4" />
                Ver mis entregables ({existingDeliverables.length})
              </Button>

              <Dialog open={mySubmittedOpen} onOpenChange={setMySubmittedOpen}>
                <DialogContent className="max-w-lg max-h-[85vh]">
                  <DialogHeader>
                    <DialogTitle>Mis entregables ({existingDeliverables.length})</DialogTitle>
                  </DialogHeader>
                  {existingDeliverables.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">No tienes entregables enviados aún.</p>
                  ) : (
                    <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
                      {existingDeliverables.map((d) => {
                        const st = (d.solution_type ?? 1) as keyof typeof SOLUTION_TYPES;
                        const sol = SOLUTION_TYPES[st] ?? SOLUTION_TYPES[1];
                        return (
                          <div key={d.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                            <span className="text-xl mt-0.5 shrink-0">{sol.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{d.name ?? 'Sin nombre'}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {d.workshop?.title ?? 'Sin taller'}
                                {d.tool_used ? ` · ${d.tool_used}` : ''}
                              </p>
                              {d.submitted_at && (
                                <p className="text-xs text-muted-foreground">
                                  {new Date(d.submitted_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                              <Badge
                                variant={d.status === 'validated' ? 'default' : 'secondary'}
                                className={d.status === 'validated' ? 'bg-success text-success-foreground text-[10px]' : 'text-[10px]'}
                              >
                                {d.status === 'validated' && 'Validado'}
                                {d.status === 'submitted' && 'Enviado'}
                                {d.status === 'needs_revision' && 'Revisar'}
                                {d.status === 'in_progress' && 'En Progreso'}
                              </Badge>
                              {d.file_url && (
                                <a
                                  href={d.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-[10px] text-primary hover:underline"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Ver entregable
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Dialog para ver instrucciones y subir entregable */}
      <Dialog open={instructionsDialogOpen} onOpenChange={setInstructionsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Entregable
            </DialogTitle>
            <DialogDescription>
              {selectedEventForInstructions?.title}
            </DialogDescription>
          </DialogHeader>
          
          {/* Instrucciones del entregable como componentes React */}
          {selectedEventForInstructions && (selectedEventForInstructions.problemaConcreto || selectedEventForInstructions.resultadoEsperado || selectedEventForInstructions.tiempoEstimado || selectedEventForInstructions.tipAdicional) && (
            <div className="space-y-3 border-b pb-4">
              {selectedEventForInstructions.problemaConcreto && (
                <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/10">
                  <p className="font-medium text-destructive flex items-center gap-2 text-sm">🎯 Problema concreto</p>
                  <p className="text-sm mt-1 text-muted-foreground">{selectedEventForInstructions.problemaConcreto}</p>
                </div>
              )}
              {selectedEventForInstructions.resultadoEsperado && (
                <div className="p-3 rounded-lg bg-success/5 border border-success/10">
                  <p className="font-medium text-success flex items-center gap-2 text-sm">✅ Resultado esperado</p>
                  <p className="text-sm mt-1 text-muted-foreground">{selectedEventForInstructions.resultadoEsperado}</p>
                </div>
              )}
              {selectedEventForInstructions.tiempoEstimado && (
                <div className="p-3 rounded-lg bg-info/5 border border-info/10">
                  <p className="font-medium text-info flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4" /> Tiempo estimado
                  </p>
                  <p className="text-sm mt-1 text-muted-foreground">{selectedEventForInstructions.tiempoEstimado}</p>
                </div>
              )}
              {selectedEventForInstructions.tipAdicional && (
                <div className="p-3 rounded-lg bg-warning/5 border border-warning/10">
                  <p className="font-medium text-warning flex items-center gap-2 text-sm">💡 Tip adicional</p>
                  <p className="text-sm mt-1 text-muted-foreground">{selectedEventForInstructions.tipAdicional}</p>
                </div>
              )}
            </div>
          )}

          {selectedEventForInstructions?.deliverableResourceUrl && (
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => setResourceModalOpen(true)}
            >
              <PlayCircle className="w-4 h-4 text-primary" />
              Ver recurso interactivo
            </Button>
          )}

          {selectedEventForInstructions?.deliverableTools && selectedEventForInstructions.deliverableTools.length > 0 && (
            <div className="space-y-2 border-b pb-4">
              <p className="text-sm font-medium flex items-center gap-2">
                <Wrench className="w-4 h-4 text-primary" />
                Herramientas IA sugeridas
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedEventForInstructions.deliverableTools.map(tool => (
                  <Badge key={tool} variant="secondary" className="text-xs">{tool}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Info del entregable ya subido — lista desplegable si hay más de uno */}
          {selectedEventForInstructions?.deliverableUploaded && (() => {
            const workshopDeliverables = existingDeliverables.filter(
              d => d.workshop_id === selectedEventForInstructions.deliverableId &&
                   (d.status === 'submitted' || d.status === 'validated' || d.status === 'needs_revision')
            );
            const latest = workshopDeliverables[0];
            const rest = workshopDeliverables.slice(1);
            return (
              <div className="space-y-2 border-b pb-4">
                {latest && <DeliverableCard d={latest} />}
                {rest.length > 0 && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full py-1">
                      <ChevronDown className="w-3.5 h-3.5" />
                      Ver {rest.length} entregable{rest.length > 1 ? 's' : ''} anterior{rest.length > 1 ? 'es' : ''}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-2 mt-1">
                      {rest.map(d => <DeliverableCard key={d.id} d={d} />)}
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            );
          })()}

          {/* Formulario de subida: mostrar si NO ha subido, o si needs_revision, o si está reemplazando */}
          {(!selectedEventForInstructions?.deliverableUploaded || selectedEventForInstructions?.deliverableStatus === 'needs_revision' || isReplacing) && (
            <>
              <div className="space-y-4 pt-2">
                {selectedEventForInstructions?.deliverableStatus === 'needs_revision' && (
                  <p className="text-sm font-medium text-foreground">Subir nueva versión:</p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="deliverable-file-modal">Archivo(s)</Label>
                  <Input 
                    id="deliverable-file-modal" 
                    type="file" 
                    multiple
                    accept={DELIVERABLE_FILE_ACCEPT}
                  />
                  <p className="text-xs text-muted-foreground">
                    PDF, Word, PowerPoint, Excel, PNG, JPG, ZIP, MD, TXT y JSON. Máximo 25 MB por archivo. HTML o código: ZIP o enlace.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deliverable-link-modal">O ingresa un enlace</Label>
                  <Input 
                    id="deliverable-link-modal" 
                    type="url" 
                    placeholder="https://..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Puedes compartir un enlace a Google Drive, Notion, Figma, etc.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deliverable-comment-modal">Comentario (opcional)</Label>
                  <Textarea
                    id="deliverable-comment-modal"
                    placeholder="Agrega un comentario o nota para el mentor..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setInstructionsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  disabled={createDeliverable.isPending || isUploading}
                  onClick={handleSubmitDeliverable}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {isUploading ? 'Subiendo...' : selectedEventForInstructions?.deliverableStatus === 'needs_revision' ? 'Re-enviar Entregable' : 'Subir Entregable'}
                </Button>
              </div>
            </>
          )}

          {/* Si ya entregó y no necesita ajustes y no está reemplazando, botones de cerrar y cambiar */}
          {selectedEventForInstructions?.deliverableUploaded && selectedEventForInstructions?.deliverableStatus !== 'needs_revision' && !isReplacing && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsReplacing(true)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Cambiar entregable
              </Button>
              <Button variant="outline" onClick={() => setInstructionsDialogOpen(false)}>
                Cerrar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para ver experto */}
      <Dialog open={expertDialogOpen} onOpenChange={setExpertDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Experto de la Sesión en Vivo</DialogTitle>
          </DialogHeader>
          {selectedExpert && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <img 
                  src={selectedExpert.photo} 
                  alt={selectedExpert.name}
                  className="w-20 h-20 rounded-full bg-muted"
                />
                <div>
                  <h3 className="text-lg font-semibold">{selectedExpert.name}</h3>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="p-0 h-auto text-primary"
                    onClick={() => window.open(selectedExpert.linkedinUrl, '_blank')}
                  >
                    <Linkedin className="w-4 h-4 mr-1" />
                    Ver perfil en LinkedIn
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {selectedExpert.profile}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sync with Google Calendar Dialog */}
      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-primary" />
              Sincronizar con Google Calendar
            </DialogTitle>
            <DialogDescription>
              Si estás inscrito en una cohorte activa, recibirás automáticamente las invitaciones de Google Calendar (con el link de Meet) en el correo asociado a tu cuenta. Usa esta sección solo si quieres agregar eventos manualmente a tu calendario.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">

            {/* Individual events */}
            <div className="p-4 border rounded-lg space-y-3">
              <h4 className="font-semibold text-foreground">Agregar eventos individuales</h4>
              <p className="text-sm text-muted-foreground">
                Haz clic en un evento para abrirlo directamente en Google Calendar.
              </p>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {futureEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No hay eventos futuros</p>
                ) : futureEvents.map(event => (
                  <button
                    key={event.id}
                    onClick={() => handleAddToGoogleCalendar(event)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                  >
                    <CalendarIcon className="w-4 h-4 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{event.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.date && format(safeParse(event.date), "d MMM yyyy", { locale: es })} • {event.startTime} - {event.endTime}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0">{event.type}</Badge>
                    <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal grande con recurso interactivo HTML del entregable */}
      <Dialog open={resourceModalOpen} onOpenChange={setResourceModalOpen}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 flex flex-col gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-primary" />
              Recurso interactivo
            </DialogTitle>
            <DialogDescription>
              {selectedEventForInstructions?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-muted/20">
            {!selectedEventForInstructions?.deliverableResourceUrl ? (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                No hay recurso disponible.
              </div>
            ) : resourceHtml === null ? (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Cargando recurso…
              </div>
            ) : (
              <iframe
                srcDoc={resourceHtml}
                title="Recurso interactivo del entregable"
                sandbox="allow-scripts allow-forms allow-popups"
                className="w-full h-full border-0 bg-background"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reproductor de contenido dentro del calendario */}
      <ContentPlayerModal
        isOpen={isPlayerOpen}
        onClose={handleClosePlayer}
        lesson={playerLesson ? {
          id: playerLesson.id,
          title: playerLesson.title,
          duration: playerLesson.duration || '',
          type: playerLesson.playerType,
          description: playerLesson.description || '',
          completed: playerLesson.completed,
          thumbnail: playerLesson.thumbnail || '',
          vimeo_player_url: playerLesson.vimeo_player_url,
          html_content: playerLesson.html_content,
          url: playerLesson.url,
          thumbnail_url: playerLesson.thumbnail_url,
        } : null}
        lessonIndex={playerLessonIndex}
        onComplete={handleLessonComplete}
        onProgressSaved={handleProgressSaved}
      />
    </div>
  );
};

export default CalendarPage;
